import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { useRaisedHands } from '../../../hooks/useRaisedHands'
import type { SharedRaisedHands } from '../context/RaisedHandsContext'

/** How often to re-check while someone in range has not settled yet (see `isSettledPeer`). */
export const SETTLE_RECHECK_MS = 1000

/**
 * True when `inProximityIds` (which always holds the local player) has anyone
 * else who passes `isSettledPeer`.
 */
export function hasConnectedPeers(
  inProximityIds: ReadonlySet<string>,
  localIdentity: string | undefined,
  isSettledPeer: (identity: string) => boolean = () => true
): boolean {
  if (!localIdentity) return false
  for (const id of inProximityIds) {
    if (id !== localIdentity && isSettledPeer(id)) return true
  }
  return false
}

/**
 * A raised hand belongs to the conversation it was raised in. It is lowered:
 *
 * - the moment raise hand stops being offered (nobody left in the room to hear
 *   them, or they are out of any room), the same signal that hides the control,
 *   so a hand is never up without a way to lower it (proximity's own miss
 *   hysteresis already absorbs a peer flickering at the edge of range);
 * - whenever the player moves into, out of or between virtual rooms, even with
 *   someone nearby on the way, so it is never carried into the next meeting.
 */
export function useLowerHandWhenConversationEnds(
  raised: boolean,
  canRaise: boolean,
  virtualRoomId: string | null,
  /** Bound to one Colyseus room; must keep its identity while that room does. */
  lower: () => void
): void {
  const raisedRef = useRef(raised)
  raisedRef.current = raised

  // One request per raise and connection: leaving a room alone trips both
  // rules, and the hand stays up here until the server's change arrives. The
  // token-fallback reconnect binds a new Room and drops whatever was queued on
  // the old one, so a request made through an earlier `lower` does not count.
  const requestedWithRef = useRef<(() => void) | null>(null)
  useEffect(() => {
    if (!raised) requestedWithRef.current = null
  }, [raised])
  const lowerOnce = useCallback(() => {
    if (requestedWithRef.current === lower) return
    requestedWithRef.current = lower
    lower()
  }, [lower])

  useEffect(() => {
    if (raised && !canRaise) lowerOnce()
  }, [raised, canRaise, lowerOnce])

  // A request still unanswered when the room was replaced went down with it.
  useEffect(() => {
    const previous = requestedWithRef.current
    if (previous && previous !== lower && raisedRef.current) lowerOnce()
  }, [lower, lowerOnce])

  const roomRef = useRef(virtualRoomId)
  useEffect(() => {
    if (roomRef.current === virtualRoomId) return
    roomRef.current = virtualRoomId
    if (raisedRef.current) lowerOnce()
  }, [virtualRoomId, lowerOnce])
}

const everyoneSettled = () => true

export interface OfficeRaisedHandsInput {
  /** The Colyseus room. */
  room: any | null
  /** Who the local player can hear right now, local player included (`useLiveKitProximity`). */
  inProximityIds: ReadonlySet<string>
  /**
   * The local LiveKit identity, which is what `inProximityIds` is keyed by.
   * Normally equal to the Colyseus session id, but a rejoin after a failed
   * reconnect gets a new session id while LiveKit still runs on the old one.
   * Defaults to the room's session id.
   */
  localIdentity?: string
  /**
   * Whether an in-range participant is really there. `useProximityState` keeps
   * a participant it has no position for "in range" for a 3 s settle grace, and
   * that grace restarts whenever a player leaves this client's state view, so in
   * large offices far-away players flicker in and out of the set. Pass a check
   * for a known position or server virtual-room co-occupancy. Default: everyone.
   */
  isSettledPeer?: (identity: string) => boolean
  ownerSessionId?: string | null
  /**
   * The local player's virtual room, `null` in open space. Raise hand exists
   * only inside a virtual room, and a change of room lowers the hand.
   */
  virtualRoomId?: string | null
}

export interface OfficeRaisedHands {
  shared: SharedRaisedHands
  /** The local player may lower other people's hands: virtual room owner or screen-share presenter (the server rule). */
  canLowerHands: boolean
}

/**
 * Raise hand for every virtual room, not only inside presentations: available
 * while the local player is in a virtual room with at least one other player
 * they can hear, and announced only to them. Not offered in open space (product
 * decision): a raised hand asks a meeting for the floor, and proximity chats
 * have no meeting to ask.
 */
export function useOfficeRaisedHands({
  room,
  inProximityIds,
  localIdentity,
  isSettledPeer = everyoneSettled,
  ownerSessionId,
  virtualRoomId = null,
}: OfficeRaisedHandsInput): OfficeRaisedHands {
  const localSessionId: string | undefined = room?.sessionId || undefined
  const self = localIdentity || localSessionId
  const inVirtualRoom = !!virtualRoomId
  const canRaiseHand = !!localSessionId && inVirtualRoom && hasConnectedPeers(inProximityIds, self, isSettledPeer)

  // Settling (a position arriving) does not change `inProximityIds`, so nothing
  // would re-render; poll gently while anyone in range is still unsettled. Only
  // inside a room: in open space the answer is "no" whoever settles.
  const [, recheck] = useReducer((n: number) => n + 1, 0)
  let hasUnsettledPeer = false
  if (inVirtualRoom) {
    for (const id of inProximityIds) {
      if (id !== self && !isSettledPeer(id)) {
        hasUnsettledPeer = true
        break
      }
    }
  }
  useEffect(() => {
    if (!hasUnsettledPeer) return
    const timer = setInterval(recheck, SETTLE_RECHECK_MS)
    return () => clearInterval(timer)
  }, [hasUnsettledPeer])

  const isSettledPeerRef = useRef(isSettledPeer)
  isSettledPeerRef.current = isSettledPeer
  const shouldAnnounce = useCallback(
    (sessionId: string) => inVirtualRoom && inProximityIds.has(sessionId) && isSettledPeerRef.current(sessionId),
    [inVirtualRoom, inProximityIds]
  )
  const hands = useRaisedHands(room, { shouldAnnounce })

  const { setLocalHandRaised } = hands
  const lowerOwnHand = useCallback(() => setLocalHandRaised(false), [setLocalHandRaised])
  useLowerHandWhenConversationEnds(hands.localHandRaised, canRaiseHand, virtualRoomId, lowerOwnHand)

  const isLocalPresenter = !!(localSessionId && room?.state?.players?.get(localSessionId)?.screenShareSessionId)
  const canLowerHands = !!localSessionId && (ownerSessionId === localSessionId || isLocalPresenter)

  const shared = useMemo<SharedRaisedHands>(
    () => ({ ...hands, canRaiseHand }),
    // `hands` is a fresh object per render; depend on its parts so consumers only re-render on change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hands.handRaisedSessionIds, hands.localHandRaised, hands.toggleHandRaise, hands.setLocalHandRaised, hands.lowerHand, canRaiseHand]
  )

  return { shared, canLowerHands }
}
